/**
 * HotelDeliverySection.tsx
 * ホテル送迎（ホテル配車＝貸出場所指定）— json_data の配車情報を表示し、スタッフが変更できる。
 *
 * - LP 予約は json_data.data に日本語キーで入ってくる（キー契約の正: booking-platform/md/hotel-delivery.md）
 * - 予約後に「やっぱりホテル配車を希望」となった場合などに、ここで あり/なし・区分・場所・返却先 を編集する。
 *   保存はパネル本体の保存ボタン（onChange で下書きの json_data を差し替えるだけ。JsonDataModal と同じ経路）
 * - json_data は文字列 / {data,types} / 旧配列 のどれでも読め、書き込み時は {data,types} に正規化される
 */
import React, { useState } from 'react';
import { SectionProps } from '../types';
import SectionHeader from '../shared/SectionHeader';
import FieldDisplay from '../shared/FieldDisplay';
import { FieldMarkWrap } from '../conflict/fieldState';
import {
  parseHotelDelivery,
  hotelDeliveryMapUrl,
  applyHotelDelivery,
  toHotelDeliveryInput,
  HOTEL_DELIVERY_RETURN_OPTIONS,
  HotelDeliveryInput,
} from '../../../lib/hotelDelivery';

const JSON_FIELDS = ['json_data'] as const;

interface HotelDeliverySectionProps extends SectionProps {
  onJsonModalOpen?: () => void;
}

const AMBER = { border: '#f59e0b', bg: '#fffbeb', text: '#78350f', sub: '#92400e', link: '#b45309' };

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: '0.85rem',
  border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', color: '#0f172a',
};
const labelStyle: React.CSSProperties = { fontSize: '0.78rem', color: '#475569', fontWeight: 600, marginBottom: 2, display: 'block' };
const btnStyle = (primary: boolean, disabled?: boolean): React.CSSProperties => ({
  padding: '4px 12px', fontSize: '0.78rem', borderRadius: 4, cursor: disabled ? 'default' : 'pointer',
  border: primary ? 'none' : '1px solid #cbd5e1',
  background: primary ? '#f59e0b' : '#fff', color: primary ? '#fff' : '#475569', fontWeight: primary ? 700 : 500,
  opacity: disabled ? 0.6 : 1,
});

export default function HotelDeliverySection({ data, onChange, isNew, disabled, onJsonModalOpen }: HotelDeliverySectionProps) {
  const [editing, setEditing] = useState(false);
  const [wanted, setWanted] = useState(false);
  const [form, setForm] = useState<HotelDeliveryInput>(() => toHotelDeliveryInput(null));

  if (isNew) return null;
  const h = parseHotelDelivery(data.json_data);
  const canEdit = !disabled && !!onChange;

  const startEdit = () => {
    setWanted(!!h);
    setForm(toHotelDeliveryInput(h));
    setEditing(true);
  };
  const commit = () => {
    onChange?.({ json_data: applyHotelDelivery(data.json_data, wanted ? form : null) });
    setEditing(false);
  };
  const set = (patch: Partial<HotelDeliveryInput>) => setForm((f) => ({ ...f, ...patch }));

  if (editing) {
    return (
      <>
        <SectionHeader title="ホテル送迎" />
        <FieldMarkWrap fields={JSON_FIELDS} chip>
          <div data-testid="hotel-delivery-edit" style={{ border: `1px solid ${AMBER.border}`, borderRadius: 6, padding: '8px 12px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <label style={labelStyle} htmlFor="hd-wanted">ホテル配車</label>
              <select id="hd-wanted" style={inputStyle} value={wanted ? 'yes' : 'no'} onChange={(e) => setWanted(e.target.value === 'yes')}>
                <option value="no">なし</option>
                <option value="yes">希望あり</option>
              </select>
            </div>
            {wanted && (
              <>
                <div>
                  <label style={labelStyle} htmlFor="hd-mode">配車区分</label>
                  <select id="hd-mode" style={inputStyle} value={form.returnOnly ? 'return_only' : 'delivery'} onChange={(e) => set({ returnOnly: e.target.value === 'return_only' })}>
                    <option value="delivery">貸出場所まで配車</option>
                    <option value="return_only">返却のみ指定（貸出は店舗）</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle} htmlFor="hd-place">{form.returnOnly ? '返却先（ホテル名など）' : '貸出場所（ホテル名など）'}</label>
                  <input id="hd-place" style={inputStyle} value={form.place} placeholder="例：◯◯ホテル那覇" onChange={(e) => set({ place: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="hd-address">住所（任意）</label>
                  <input id="hd-address" style={inputStyle} value={form.address} placeholder="例：沖縄県那覇市…" onChange={(e) => set({ address: e.target.value })} />
                </div>
                {!form.returnOnly && (
                  <div>
                    <label style={labelStyle} htmlFor="hd-return">返却場所</label>
                    <select id="hd-return" style={inputStyle} value={form.returnPlace} onChange={(e) => set({ returnPlace: e.target.value })}>
                      <option value="">未設定</option>
                      {HOTEL_DELIVERY_RETURN_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o === 'ホテル' ? 'ホテル（貸出場所と同じ）' : o}</option>
                      ))}
                    </select>
                    {form.returnPlace === 'その他' && (
                      <input
                        aria-label="その他の返却場所"
                        style={{ ...inputStyle, marginTop: 4 }}
                        value={form.returnOther}
                        placeholder="例：那覇港"
                        onChange={(e) => set({ returnOther: e.target.value })}
                      />
                    )}
                  </div>
                )}
              </>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" style={btnStyle(false)} onClick={() => setEditing(false)}>キャンセル</button>
              <button type="button" style={btnStyle(true)} onClick={commit}>反映</button>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>「反映」後、パネル右上の保存で確定します。</div>
          </div>
        </FieldMarkWrap>
      </>
    );
  }

  if (!h) {
    return (
      <>
        <SectionHeader title="ホテル送迎" />
        <FieldDisplay
          label="ホテル配車"
          value="なし"
          fields={JSON_FIELDS}
          color="#94a3b8"
          onClick={canEdit ? startEdit : undefined}
        />
        {canEdit && (
          <button type="button" style={{ ...btnStyle(false), alignSelf: 'flex-start', marginTop: 2 }} onClick={startEdit}>
            ＋ ホテル配車を追加
          </button>
        )}
      </>
    );
  }

  const mapUrl = hotelDeliveryMapUrl(h);
  const returnText = h.returnOnly
    ? null
    : (h.returnPlace === 'その他' ? `その他（${h.returnOther || '未入力'}）` : (h.returnPlace || '未設定'));

  return (
    <>
      <SectionHeader title="ホテル送迎" />
      <FieldMarkWrap fields={JSON_FIELDS} chip>
        <div
          data-testid="hotel-delivery-box"
          style={{
            border: `1px solid ${AMBER.border}`, borderLeft: `4px solid ${AMBER.border}`, background: AMBER.bg,
            borderRadius: 6, padding: '8px 12px', fontSize: '0.85rem', color: AMBER.text,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, background: AMBER.border, color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: '0.78rem' }}>
              ホテル配車 希望
            </span>
            <span style={{ fontWeight: 600 }}>{h.mode}</span>
          </div>
          <Row label={h.returnOnly ? '返却先' : '貸出場所'} value={h.place || '未入力'} strong />
          {h.address && <Row label="住所" value={h.address} />}
          {!h.returnOnly && <Row label="返却場所" value={returnText || '-'} />}
          {h.returnOnly && (
            <div style={{ fontSize: '0.78rem', color: AMBER.sub }}>
              貸出は店舗。返却時に上記の場所へ車両を届ける（お客様が持ち込む形）
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 2, fontSize: '0.8rem', flexWrap: 'wrap' }}>
            {mapUrl && (
              <a href={mapUrl} target="_blank" rel="noopener noreferrer" style={{ color: AMBER.link, textDecoration: 'underline' }}>
                地図を開く{h.mapUrl ? '' : '（名称/住所で検索）'}
              </a>
            )}
            {canEdit && (
              <LinkButton onClick={startEdit}>変更</LinkButton>
            )}
            {onJsonModalOpen && (
              <LinkButton onClick={onJsonModalOpen} disabled={disabled}>その他データ</LinkButton>
            )}
          </div>
        </div>
      </FieldMarkWrap>
    </>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ color: AMBER.sub, minWidth: 64 }}>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 500, wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

function LinkButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{ background: 'none', border: 'none', padding: 0, color: AMBER.sub, textDecoration: 'underline', cursor: disabled ? 'default' : 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}
    >
      {children}
    </button>
  );
}
